import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { PDFDocument, PDFFont, StandardFonts, rgb } from 'pdf-lib'
import PDFDoc from '../components/Vouchers/PDFDoc'

// Builds a single downloadable PDF for a voucher: page 1 is the voucher
// summary (PDFDoc), followed by one or more pages per receipt — real pages
// copied in for PDF attachments, a full-page render for images, and a
// fallback note for anything that can't be previewed or fetched.

const A4_WIDTH = 595.28
const A4_HEIGHT = 841.89
const PAGE_MARGIN = 40

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
const IMAGE_MIME_TYPES: Record<string, string> = {
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
}

export type VoucherAttachment = {
  id: string
  path: string
}

function getExtension(fileName: string): string {
  return fileName?.split('.').pop()?.toLowerCase() || ''
}

async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch attachment (status ${response.status})`)
  }
  return response.arrayBuffer()
}

// pdf-lib can only embed JPEG/PNG, so any other browser-decodable image
// format (gif, bmp, webp) is rasterized to PNG via canvas first.
async function convertImageToPng(
  bytes: ArrayBuffer,
  mimeType: string,
): Promise<ArrayBuffer> {
  const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }))
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Could not decode image'))
      img.src = objectUrl
    })

    const canvas = document.createElement('canvas')
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas is not supported in this browser')
    ctx.drawImage(image, 0, 0)

    const pngBlob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png'),
    )
    if (!pngBlob) throw new Error('Could not encode image as PNG')
    return pngBlob.arrayBuffer()
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

// Standard fonts only support WinAnsi; file names (e.g. macOS NFD names with
// combining marks like U+0308) can contain characters they cannot encode.
function toWinAnsiSafe(font: PDFFont, text: string): string {
  return Array.from(text.normalize('NFC'))
    .map((ch) => {
      try {
        font.encodeText(ch)
        return ch
      } catch {
        return '?'
      }
    })
    .join('')
}

function addNotePage(doc: PDFDocument, font: PDFFont, fileName: string, message: string) {
  const page = doc.addPage([A4_WIDTH, A4_HEIGHT])
  page.drawText(toWinAnsiSafe(font, fileName), {
    x: PAGE_MARGIN,
    y: A4_HEIGHT - PAGE_MARGIN,
    size: 12,
    font,
    color: rgb(0.17, 0.23, 0.36),
  })
  page.drawText(toWinAnsiSafe(font, message), {
    x: PAGE_MARGIN,
    y: A4_HEIGHT - PAGE_MARGIN - 22,
    size: 10,
    font,
    color: rgb(0.5, 0.5, 0.5),
    maxWidth: A4_WIDTH - PAGE_MARGIN * 2,
  })
}

async function addImagePage(
  doc: PDFDocument,
  font: PDFFont,
  fileName: string,
  bytes: ArrayBuffer,
  extension: string,
) {
  const isNativelySupported = extension === 'jpg' || extension === 'jpeg' || extension === 'png'
  const embedBytes = isNativelySupported
    ? bytes
    : await convertImageToPng(bytes, IMAGE_MIME_TYPES[extension] || 'image/png')

  const image =
    extension === 'jpg' || extension === 'jpeg'
      ? await doc.embedJpg(embedBytes)
      : await doc.embedPng(embedBytes)

  const page = doc.addPage([A4_WIDTH, A4_HEIGHT])
  page.drawText(toWinAnsiSafe(font, fileName), {
    x: PAGE_MARGIN,
    y: A4_HEIGHT - PAGE_MARGIN + 12,
    size: 10,
    font,
    color: rgb(0.4, 0.4, 0.4),
  })

  const maxWidth = A4_WIDTH - PAGE_MARGIN * 2
  const maxHeight = A4_HEIGHT - PAGE_MARGIN * 2 - 20
  const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1)
  const width = image.width * scale
  const height = image.height * scale

  page.drawImage(image, {
    x: (A4_WIDTH - width) / 2,
    y: (A4_HEIGHT - height) / 2 - 10,
    width,
    height,
  })
}

async function addPdfPages(doc: PDFDocument, bytes: ArrayBuffer) {
  const sourceDoc = await PDFDocument.load(bytes, { ignoreEncryption: true })
  const copiedPages = await doc.copyPages(sourceDoc, sourceDoc.getPageIndices())
  copiedPages.forEach((page) => doc.addPage(page))
}

export async function buildVoucherPdf(
  voucher: any,
  attachments: VoucherAttachment[],
  fetchSignedUrl: (attachmentId: string) => Promise<string>,
): Promise<Uint8Array> {
  const detailsBlob = await pdf(
    React.createElement(PDFDoc, { voucher }) as React.ReactElement,
  ).toBlob()
  const detailsBytes = await detailsBlob.arrayBuffer()

  const finalDoc = await PDFDocument.create()
  const detailsDoc = await PDFDocument.load(detailsBytes)
  const detailsPages = await finalDoc.copyPages(detailsDoc, detailsDoc.getPageIndices())
  detailsPages.forEach((page) => finalDoc.addPage(page))

  const font = await finalDoc.embedFont(StandardFonts.Helvetica)

  for (const attachment of attachments) {
    const fileName = attachment.path?.split('/').pop() || attachment.path || 'attachment'
    const extension = getExtension(fileName)

    try {
      const signedUrl = await fetchSignedUrl(attachment.id)
      const bytes = await fetchArrayBuffer(signedUrl)

      if (extension === 'pdf') {
        await addPdfPages(finalDoc, bytes)
      } else if (IMAGE_EXTENSIONS.includes(extension)) {
        await addImagePage(finalDoc, font, fileName, bytes, extension)
      } else {
        addNotePage(
          finalDoc,
          font,
          fileName,
          'Preview not available for this file type. Download it separately from the voucher.',
        )
      }
    } catch (err) {
      console.error(`Could not include attachment "${fileName}" in voucher PDF`, err)
      addNotePage(
        finalDoc,
        font,
        fileName,
        'This attachment could not be included automatically. Download it separately from the voucher.',
      )
    }
  }

  return finalDoc.save()
}

export function downloadPdfBytes(bytes: Uint8Array, fileName: string) {
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
